// AvailabilityForm Component
// Form for managing availability and preferences

import React, { useState } from 'react';
import { FaCalendarCheck, FaMapMarkerAlt, FaDollarSign } from 'react-icons/fa';
import styles from './AvailabilityForm.module.css';
import LocationPicker from '../../../components/LocationPicker/LocationPicker';

const AVAILABILITY_STATUS_OPTIONS = [
  { value: 'actively_looking', label: 'Actively Looking', color: 'green' },
  { value: 'open_to_offers', label: 'Open to Offers', color: 'blue' },
  { value: 'not_looking', label: 'Not Looking', color: 'gray' }
];

const COMMITMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'tournament', label: 'Tournament Only' },
  { value: 'flexible', label: 'Flexible' }
];

const LEVEL_OPTIONS = [
  'Youth Academy',
  'High School',
  'College/University',
  'Semi-Professional',
  'Professional',
  'National Team',
  'International'
];

const AvailabilityForm = ({ data, onChange }) => {
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  
  // Ensure data exists
  if (!data) {
    return <div>Loading form...</div>;
  }

  // Handle input changes
  const handleChange = (field, value) => {
    const updatedData = { ...data };
    
    // Handle nested fields (supports multiple levels)
    if (field.includes('.')) {
      const parts = field.split('.');
      let current = updatedData;
      
      // Navigate to the parent object
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) {
          current[parts[i]] = {};
        } else {
          current[parts[i]] = { ...current[parts[i]] };
        }
        current = current[parts[i]];
      }
      
      // Set the final value
      current[parts[parts.length - 1]] = value;
    } else {
      updatedData[field] = value;
    }
    
    onChange(updatedData);
  };

  // Handle array changes
  const handleArrayChange = (field, value, checked) => {
    const currentArray = data[field] || [];
    let newArray;
    
    if (checked) {
      newArray = [...currentArray, value];
    } else {
      newArray = currentArray.filter(item => item !== value);
    }
    
    handleChange(field, newArray);
  };

  // Handle location add
  const handleLocationAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      e.preventDefault();
      const locations = data.preferredLocations || [];
      if (!locations.includes(e.target.value.trim())) {
        handleChange('preferredLocations', [...locations, e.target.value.trim()]);
      }
      e.target.value = '';
    }
  };

  // Remove location
  const removeLocation = (location) => {
    const locations = data.preferredLocations || [];
    handleChange('preferredLocations', locations.filter(l => l !== location));
  };
  
  // Handle location selection from map
  const handleLocationSelect = (locationData) => {
    const locationString = [locationData.city, locationData.state, locationData.country]
      .filter(Boolean)
      .join(', ');
    
    const locations = data.preferredLocations || [];
    if (locationString && !locations.includes(locationString)) {
      handleChange('preferredLocations', [...locations, locationString]);
    }
    setShowLocationPicker(false);
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.sectionTitle}>
        <FaCalendarCheck /> Availability & Preferences
      </h2>

      <p className={styles.description}>
        Let clubs know about your availability and what you're looking for in your next opportunity.
      </p>

      {/* Availability Status */}
      <div className={styles.statusSection}>
        <h3 className={styles.subsectionTitle}>Current Status</h3>
        <div className={styles.statusOptions}>
          {AVAILABILITY_STATUS_OPTIONS.map(option => (
            <div 
              key={option.value} 
              className={`${styles.statusCard} ${data.status === option.value ? styles.active : ''}`}
              onClick={() => {
                handleChange('status', option.value);
                // Add a subtle haptic-like feedback via CSS animation
                const card = document.querySelector(`[data-status="${option.value}"]`);
                if (card) {
                  card.style.animation = 'none';
                  setTimeout(() => {
                    card.style.animation = 'selectPulse 0.3s ease';
                  }, 10);
                }
              }}
              role="button"
              tabIndex={0}
              data-status={option.value}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleChange('status', option.value);
                }
              }}
            >
              <div className={`${styles.statusIndicator} ${styles[option.color]}`}></div>
              <span className={styles.statusLabel}>{option.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Start Date */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Available Starting From</label>
        <input
          type="date"
          className={styles.input}
          value={data.startDate || ''}
          onChange={(e) => handleChange('startDate', e.target.value)}
          min={new Date().toISOString().split('T')[0]}
        />
        <p className={styles.helperText}>
          When can you start with a new club/team?
        </p>
      </div>

      {/* Location Preferences */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <FaMapMarkerAlt /> Preferred Locations
        </label>
        
        {!showLocationPicker ? (
          <>
            <input
              type="text"
              className={styles.input}
              placeholder="Type a location and press Enter, or pick from map..."
              onKeyPress={handleLocationAdd}
            />
            
            {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && (
              <button
                type="button"
                className={styles.pickLocationButton}
                onClick={() => setShowLocationPicker(true)}
              >
                <FaMapMarkerAlt />
                Pick Location from Map
              </button>
            )}
            
            {data.preferredLocations?.length > 0 && (
              <div className={styles.locationTags}>
                {data.preferredLocations.map(location => (
                  <span key={location} className={styles.locationTag}>
                    {location}
                    <button
                      type="button"
                      onClick={() => removeLocation(location)}
                      className={styles.removeTag}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <LocationPicker
              value={null}
              onLocationSelect={handleLocationSelect}
            />
            <button
              type="button"
              className={styles.cancelPickerButton}
              onClick={() => setShowLocationPicker(false)}
            >
              Cancel
            </button>
          </>
        )}

        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={data.willingToRelocate || false}
            onChange={(e) => handleChange('willingToRelocate', e.target.checked)}
          />
          <span className={styles.checkmark}></span>
          Willing to relocate
        </label>

        {data.willingToRelocate && (
          <div className={styles.travelRadius}>
            <label className={styles.label}>Maximum travel distance</label>
            <div className={styles.inputGroup}>
              <input
                type="number"
                className={styles.input}
                value={data.travelRadius || ''}
                onChange={(e) => handleChange('travelRadius', e.target.value)}
                placeholder="Distance"
                min="0"
              />
              <select
                className={styles.unitSelect}
                value={data.travelRadiusUnit || 'miles'}
                onChange={(e) => handleChange('travelRadiusUnit', e.target.value)}
              >
                <option value="miles">miles</option>
                <option value="km">km</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Preferred Levels */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Preferred Competition Levels</label>
        <div className={styles.checkboxGrid}>
          {LEVEL_OPTIONS.map(level => (
            <label key={level} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={(data.preferredLevels || []).includes(level)}
                onChange={(e) => handleArrayChange('preferredLevels', level, e.target.checked)}
              />
              <span className={styles.checkmark}></span>
              {level}
            </label>
          ))}
        </div>
      </div>

      {/* Commitment Type */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Preferred Commitment</label>
        <div className={styles.radioGroup}>
          {COMMITMENT_TYPES.map(type => (
            <label key={type.value} className={styles.radioLabel}>
              <input
                type="radio"
                name="commitment"
                value={type.value}
                checked={data.preferredCommitment === type.value}
                onChange={(e) => handleChange('preferredCommitment', e.target.value)}
              />
              <span className={styles.radioCustom}></span>
              {type.label}
            </label>
          ))}
        </div>
      </div>

      {/* Compensation */}
      <div className={styles.formGroup}>
        <label className={styles.label}>
          <FaDollarSign /> Compensation Expectations
        </label>
        <div className={styles.compensationGrid}>
          <select
            className={styles.select}
            value={data.compensationType || ''}
            onChange={(e) => handleChange('compensationType', e.target.value)}
          >
            <option value="">Select type</option>
            <option value="salary">Annual Salary</option>
            <option value="hourly">Hourly Rate</option>
            <option value="scholarship">Scholarship</option>
            <option value="stipend">Stipend</option>
            <option value="volunteer">Volunteer</option>
          </select>

          {data.compensationType && data.compensationType !== 'volunteer' && (
            <div className={styles.inputGroup}>
              <input
                type="number"
                className={styles.input}
                value={data.minimumCompensation || ''}
                onChange={(e) => handleChange('minimumCompensation', e.target.value)}
                placeholder="Minimum amount"
                min="0"
              />
              <select
                className={styles.unitSelect}
                value={data.compensationCurrency || 'USD'}
                onChange={(e) => handleChange('compensationCurrency', e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>
          )}
        </div>
        
        {data.compensationType && data.compensationType !== 'volunteer' && (
          <p className={styles.helperText}>
            This information is only visible to clubs when privacy settings allow
          </p>
        )}
      </div>

      {/* Additional Notes */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Additional Notes</label>
        <textarea
          className={styles.textarea}
          value={data.notes || ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Any other preferences or requirements? (e.g., specific training facilities, academic requirements, etc.)"
          rows={4}
          maxLength={500}
        />
        <p className={styles.helperText}>
          {500 - (data.notes?.length || 0)} characters remaining
        </p>
      </div>

      {/* Career Goals */}
      <div className={styles.formGroup}>
        <label className={styles.label}>Career Goals</label>
        <textarea
          className={styles.textarea}
          value={data.careerGoals || ''}
          onChange={(e) => handleChange('careerGoals', e.target.value)}
          placeholder="What are your short-term and long-term career goals? What type of environment are you looking for?"
          rows={4}
          maxLength={1000}
        />
        <p className={styles.helperText}>
          Help clubs understand your aspirations and how they can help you achieve them
        </p>
      </div>
    </div>
  );
};

export default AvailabilityForm;
